// ABOUTME: Centralized error handling service for Oracle React app
// ABOUTME: Captures, classifies and logs errors to PocketBase with severity levels

import pb from './pocketbaseClient';

class ErrorHandler {
  static SEVERITY_LEVELS = {
    CRITICAL: 'critical',
    WARNING: 'warning',
    INFO: 'info'
  };

  static ERROR_TYPES = {
    CLAUDE_API: 'claude_api',
    AUTH: 'authentication',
    DATABASE: 'database',
    NAVIGATION: 'navigation',
    VALIDATION: 'validation',
    NETWORK: 'network',
    SYSTEM: 'system'
  };

  static getSeverityFromErrorType(errorType) {
    const severityMap = {
      [this.ERROR_TYPES.CLAUDE_API]: this.SEVERITY_LEVELS.CRITICAL,
      [this.ERROR_TYPES.AUTH]: this.SEVERITY_LEVELS.CRITICAL,
      [this.ERROR_TYPES.DATABASE]: this.SEVERITY_LEVELS.CRITICAL,
      [this.ERROR_TYPES.NETWORK]: this.SEVERITY_LEVELS.WARNING,
      [this.ERROR_TYPES.NAVIGATION]: this.SEVERITY_LEVELS.WARNING,
      [this.ERROR_TYPES.VALIDATION]: this.SEVERITY_LEVELS.INFO,
      [this.ERROR_TYPES.SYSTEM]: this.SEVERITY_LEVELS.WARNING
    };

    return severityMap[errorType] || this.SEVERITY_LEVELS.WARNING;
  }

  static async captureError(errorType, message, userId = null, additionalData = {}) {
    const errorData = {
      errorType,
      message,
      severity: this.getSeverityFromErrorType(errorType),
      userId: userId || null, // TD3-191: Relation field - use null instead of 'anonymous'
      timeStamp: new Date().toISOString(),  // Fixed field name to match PocketBase
      userAgent: navigator?.userAgent || 'unknown',
      url: window?.location?.href || 'unknown',
      metadata: additionalData, // TD3-191: Fixed field name from additionalData to metadata (PocketBase auto-handles JSON)
      environment: process.env.NODE_ENV || 'development'
    };

    try {
      // Log to console for development
      if (process.env.NODE_ENV === 'development') {
        console.error(`[${errorData.severity.toUpperCase()}] ${errorType}:`, message, additionalData);
      }

      // Save to PocketBase error_logs collection
      await pb.collection('error_logs').create(errorData);

      // Trigger alert service for critical errors
      if (errorData.severity === this.SEVERITY_LEVELS.CRITICAL) {
        const { AlertService } = await import('./alertService');
        AlertService.sendAlert(errorData);
      }

      return {
        success: true,
        errorId: `${Date.now()}_${errorType}`,
        severity: errorData.severity
      };
    } catch (loggingError) {
      // Fallback logging if PocketBase fails
      console.error('Failed to log error to PocketBase:', loggingError);
      console.error('Original error:', { errorType, message, additionalData });

      return {
        success: false,
        errorId: `${Date.now()}_${errorType}_fallback`,
        severity: errorData.severity,
        fallback: true
      };
    }
  }

  static createErrorResponse(message, errorCode = 'GENERIC_ERROR', details = {}) {
    return {
      success: false,
      error: {
        code: errorCode,
        message,
        details,
        timestamp: new Date().toISOString()
      }
    };
  }

  static createSuccessResponse(data = {}, message = 'Operation successful') {
    return {
      success: true,
      data,
      message,
      timestamp: new Date().toISOString()
    };
  }

  static async handleAsyncError(asyncOperation, errorType, userId = null, context = {}) {
    try {
      const result = await asyncOperation();
      return this.createSuccessResponse(result);
    } catch (error) {
      await this.captureError(
        errorType,
        error.message || 'Unknown error occurred',
        userId,
        {
          stack: error.stack,
          context,
          errorName: error.name
        }
      );

      return this.createErrorResponse(
        error.message || 'An unexpected error occurred',
        error.code || 'ASYNC_OPERATION_FAILED',
        { context, errorType }
      );
    }
  }

  static sanitizeErrorForUser(error) {
    const userFriendlyMessages = {
      [this.ERROR_TYPES.CLAUDE_API]: 'Ocorreu um problema com a geração de conteúdo. Tente novamente.',
      [this.ERROR_TYPES.AUTH]: 'Problema de autenticação. Faça login novamente.',
      [this.ERROR_TYPES.DATABASE]: 'Erro ao salvar dados. Verifique sua conexão.',
      [this.ERROR_TYPES.NETWORK]: 'Problema de conexão. Verifique sua internet.',
      [this.ERROR_TYPES.NAVIGATION]: 'Erro de navegação. Recarregue a página.',
      [this.ERROR_TYPES.VALIDATION]: 'Dados inválidos fornecidos.'
    };

    return userFriendlyMessages[error.errorType] || 'Ocorreu um erro inesperado.';
  }
}

export default ErrorHandler;